import { IconButton, Pagination, styled, Tooltip } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import {
  DataGridPro,
  getGridBooleanOperators,
  getGridDateOperators,
  getGridNumericOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridColDef,
  GridColumnVisibilityModel,
  GridDensity,
  GridFilterItem,
  GridLogicOperator,
  GridOverlay,
  gridPageSelector,
  gridFilteredSortedRowIdsSelector,
  GridRowSelectionModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridValueFormatter,
  GRID_CHECKBOX_SELECTION_FIELD,
  useGridApiContext,
  useGridSelector,
  GridApiPro
} from '@mui/x-data-grid-pro';
import { DataGridProps, GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import { Dispatch, SetStateAction, useEffect, useMemo, useState, useRef } from 'react';
import { PATH_DASHBOARD } from 'routes/paths';
import {
  setColumnVisibility,
  setColumnOrder,
  setDensity,
  setFiltering,
  setPinnedColumns,
  setSorting,
  defaultSorting,
  DataGridKey,
  defaultFiltering
} from '../../redux/slices/datagrid';
import { RootState, useSelector } from '../../redux/store';
import { useTheme } from '@mui/material/styles';
import { useCustomDispatchWithoutPromise } from 'redux/dispatch';
import Icon from 'components/icons/Icon';
import { useTranslator } from 'translation/useTranslator';
import { TranslationWithHtml } from 'components/text/TranslationWithHtml';
import { getTranslationColor } from 'translation/translationStyle';
import { InfoTooltip } from '@nps.today/component-library';

const passThroughValueFormatter: GridValueFormatter = (value) =>
  value === undefined || value === null ? '' : String(value);

const componentDataTestId = 'custom-datagrid';

const normalizeRowSelectionModel = (model?: GridRowSelectionModel) => {
  if (!model) return undefined;
  if (Array.isArray(model)) {
    return { type: 'include', ids: new Set(model) } as GridRowSelectionModel;
  }
  return model as GridRowSelectionModel;
};

type CustomGridToolbarProps = {
  dataGridKey?: DataGridKey;
  setGridDensity?: Dispatch<SetStateAction<GridDensity | undefined>>;
  initialFilterModel?: GridFilterModel;
  setFilterModel?: Dispatch<SetStateAction<GridFilterModel | undefined>>;
  setSortModel?: Dispatch<SetStateAction<GridSortModel | undefined>>;
  intitialColumnsHidden?: GridColumnVisibilityModel;
  setRerenderColumns?: Dispatch<SetStateAction<any>>;
  setUpdateDataGrid?: Dispatch<SetStateAction<any>>;
};

export const CustomGridToolbar = ({
  dataGridKey,
  setGridDensity,
  initialFilterModel,
  setFilterModel,
  setSortModel,
  intitialColumnsHidden,
  setRerenderColumns,
  setUpdateDataGrid
}: CustomGridToolbarProps) => {
  const translator = useTranslator();
  const customDispatch = useCustomDispatchWithoutPromise();
  const onClickResetTable = () => {
    //filtering
    const newFilterModel =
      initialFilterModel ||
      (dataGridKey
        ? { ...defaultFiltering[dataGridKey] }
        : {
            items: [],
            linkOperator: GridLogicOperator.And,
            quickFilterLogicOperator: GridLogicOperator.And,
            quickFilterValues: []
          });

    setFilterModel?.(newFilterModel);
    customDispatch({
      action: setFiltering,
      actionParameters: [dataGridKey, newFilterModel],
      disableSuccessMessage: true
    });

    //sorting
    const newSortModel = dataGridKey ? defaultSorting[dataGridKey] : [];
    setSortModel?.(newSortModel);
    customDispatch({
      action: setSorting,
      actionParameters: [dataGridKey, newSortModel],
      disableSuccessMessage: true
    });

    //density
    setGridDensity?.('standard');
    customDispatch({
      action: setDensity,
      actionParameters: [dataGridKey, 'standard'],
      disableSuccessMessage: true
    });

    //pined columns
    customDispatch({
      action: setPinnedColumns,
      actionParameters: [dataGridKey, undefined],
      disableSuccessMessage: true
    });

    //column visibility
    customDispatch({
      action: setColumnVisibility,
      actionParameters: [dataGridKey, intitialColumnsHidden || undefined],
      disableSuccessMessage: true
    });

    //column order
    customDispatch({
      action: setColumnOrder,
      actionParameters: [dataGridKey, undefined],
      disableSuccessMessage: true
    });

    //rerender columns
    setRerenderColumns?.((prevState: any) => !prevState);

    setUpdateDataGrid?.((prevState: number) => prevState + 1);
  };

  const dataGridEntityType = useMemo((): string => {
    return dataGridKey ? dataGridKey.toLowerCase() : '';
  }, [dataGridKey]);

  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton
        slotProps={{
          button: {
            'data-testid': `${dataGridEntityType}-${componentDataTestId}-toolbar-columns-button`
          }
        }}
      />
      <GridToolbarFilterButton
        slotProps={{
          button: {
            'data-testid': `${dataGridEntityType}-${componentDataTestId}-toolbar-filters-button`
          }
        }}
      />
      <GridToolbarDensitySelector
        slotProps={{
          button: {
            'data-testid': `${dataGridEntityType}-${componentDataTestId}-toolbar-density-button`
          }
        }}
      />
      <CustomGridToolbarExport
        dataTestId={`${dataGridEntityType}-${componentDataTestId}-toolbar-export-button`}
      />
      {dataGridKey && (
        <Tooltip
          title={
            <div style={{ maxWidth: '10rem' }}>
              <span>{translator.resetAllTableFilters()}</span>
            </div>
          }
        >
          <ResetTableSettingsButton onClick={onClickResetTable}>
            <Icon type="reset" />
          </ResetTableSettingsButton>
        </Tooltip>
      )}
    </GridToolbarContainer>
  );
};

const CustomGridToolbarSlot = (props: CustomGridToolbarProps) => <CustomGridToolbar {...props} />;

type CustomGridToolbarExportProps = {
  dataTestId?: string;
};

const CustomGridToolbarExport = ({ dataTestId }: CustomGridToolbarExportProps) => {
  const translator = useTranslator();
  const theme = useTheme();

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <GridToolbarExport
        slotProps={{
          button: {
            'data-testid': dataTestId
          }
        }}
      />
      <InfoTooltip
        description={
          <TranslationWithHtml
            translation={translator.exportTableDescription(
              PATH_DASHBOARD.reports.root,
              getTranslationColor(theme.palette.primary.main)
            )}
            rawText
          />
        }
      />
    </div>
  );
};

const ResetTableSettingsButton = styled(IconButton)({
  marginLeft: 'auto'
});

export function CustomLoadingOverlay() {
  return (
    <GridOverlay>
      <div style={{ position: 'absolute', top: 0, width: '100%' }}>
        <LinearProgress />
      </div>
    </GridOverlay>
  );
}

type CustomPaginationProps = {
  onPageChange?: (newPageNumber: number) => void;
};
export const CustomPagination = ({ onPageChange }: CustomPaginationProps) => {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const visibleRowIds = useGridSelector(apiRef, gridFilteredSortedRowIdsSelector);
  const rowsMatchingFilter = visibleRowIds.length;
  const pageSize = apiRef.current.state.pagination.paginationModel?.pageSize ?? 25;
  const totalPages = Math.ceil(rowsMatchingFilter / pageSize);

  useEffect(() => {
    if (page + 1 > totalPages) {
      apiRef.current.setPage(0);
    }
  }, [page, totalPages, apiRef]);

  return (
    <Pagination
      color="primary"
      count={totalPages}
      page={page + 1} //when filtering, and on a page that is no longer avilable, gå to first page
      onChange={(event, value) => {
        const newPage = value - 1;
        if (onPageChange) {
          onPageChange(newPage);
        }
        apiRef.current.setPage(newPage);
      }}
    />
  );
};

const gridStringOperators = getGridStringOperators()?.map((operator) => operator.value) || [];
const gridNumericOperators = getGridNumericOperators()?.map((operator) => operator.value) || [];
const gridDateOperators = getGridDateOperators()?.map((operator) => operator.value) || [];

const getPinnedFields = (pinned?: { left?: any[]; right?: any[] }) => {
  if (!pinned) return undefined;
  const mapField = (item: any) => (typeof item === 'string' ? item : item?.field);
  return {
    left: pinned.left?.map(mapField).filter(Boolean),
    right: pinned.right?.map(mapField).filter(Boolean)
  } as any;
};

const getFilterModelItemType = (operator?: string) => {
  //string or date
  if (operator === 'isEmpty' || operator === 'isNotEmpty') {
    return 'stringOrDate';
  }

  //string
  if (!operator || gridStringOperators.includes(operator)) {
    return 'string';
  }
  //number
  if (gridNumericOperators.includes(operator)) {
    return 'number';
  }
  //date
  if (gridDateOperators.includes(operator)) {
    return 'date';
  }
};

const hasVisibleHideableColumn = (
  hideableColumns: GridColDef[],
  currentVisibility: GridColumnVisibilityModel
) => hideableColumns.some((column) => currentVisibility[column.field] !== false);

const isPanelToggleAllModel = (
  model: GridColumnVisibilityModel,
  hideableColumns: GridColDef[],
  nonHideableColumns: Set<string>,
  columnModelKeys: string[]
) => {
  const hasHideableColumns = hideableColumns.length > 0;
  const hasModelKeys = columnModelKeys.length > 0;
  const onlyNonHideableKeys = columnModelKeys.every((field) => nonHideableColumns.has(field));
  const allModelKeysVisible = columnModelKeys.every((field) => model[field] === true);

  return hasHideableColumns && hasModelKeys && onlyNonHideableKeys && allModelKeysVisible;
};

export type CustomDataGridProps = {
  dataGridKey: DataGridKey;
  initialFilterModel?: GridFilterModel;
  intitialColumnsHidden?: GridColumnVisibilityModel;
  height?: string;
  other?: any;
};

const getGridDensityInPixels = (density?: GridDensity) => {
  switch (density) {
    case 'compact':
      return 36;
    case 'comfortable':
      return 67;
    case 'standard':
    default:
      return 52;
  }
};

const getValidFilter = (filterModel: GridFilterModel, columns: readonly GridColDef[]) => {
  let validFilterItems: GridFilterItem[] = [];

  if (!filterModel?.items) return;

  //loop through filter value
  filterModel.items.forEach((filterItem) => {
    const column = columns.find((column) => column.field === filterItem.field);
    let filterItemType;
    if (column?.type === 'singleSelect') {
      filterItemType = 'singleSelect';
    } else if (column?.type === 'boolean') {
      filterItemType = 'boolean';
    } else {
      filterItemType = getFilterModelItemType(filterItem.operator);
    }

    //if the column does not exsists
    if (!column) {
      return;
    }

    //filtertype is not string with no specific type on the column (string is default type on columns)
    if (filterItemType !== 'string' && filterItemType !== 'stringOrDate' && !column?.type) {
      return;
    }

    //if the filtertype is not what the column type is, or
    if (
      (filterItemType !== 'stringOrDate' &&
        filterItemType !== column?.type &&
        filterItemType !== 'string') ||
      (filterItemType === 'string' && column?.type) ||
      (filterItemType === 'stringOrDate' &&
        filterItem.operator !== 'isEmpty' &&
        filterItem.operator !== 'isNotEmpty')
    ) {
      return;
    }

    //filterItem is valid
    validFilterItems.push({ ...filterItem, operator: filterItem.operator });
  });

  return { ...filterModel, items: [...validFilterItems] };
};

/// A Data grid that saves its state in localstorage
export function CustomDataGrid({
  dataGridKey,
  columns,
  initialFilterModel,
  intitialColumnsHidden,
  height,
  ...other
}: CustomDataGridProps & DataGridProps) {
  const {
    selectionModel: legacySelectionModel,
    onSelectionModelChange: legacyOnSelectionModelChange,
    rowSelectionModel: controlledRowSelectionModel,
    onRowSelectionModelChange: controlledOnRowSelectionModelChange,
    disableRowSelectionOnClick,
    ...restOther
  } = other as any;
  const customDispatch = useCustomDispatchWithoutPromise();
  const { density, sorting, filtering, columnVisibility, pinnedColumns, columnOrder } = useSelector(
    (state: RootState) => state.datagrid
  );
  const [rerenderColumns, setRerenderColumns] = useState(0);
  const ref = useRef<GridApiPro | null>(null);
  const normalizedRowSelectionModel = normalizeRowSelectionModel(
    controlledRowSelectionModel ?? legacySelectionModel
  );
  const handleRowSelectionModelChange =
    controlledOnRowSelectionModelChange || legacyOnSelectionModelChange
      ? (newModel: GridRowSelectionModel) => {
          controlledOnRowSelectionModelChange?.(newModel);
          if (legacyOnSelectionModelChange) {
            legacyOnSelectionModelChange(Array.from(newModel.ids ?? []));
          }
        }
      : undefined;

  //create columnsVisibilityModel
  let columnsVisibilyToUse = {};
  //if no visibility is set, use initial visibility (if any)
  if (columnVisibility[dataGridKey]) {
    columnsVisibilyToUse = { ...columnVisibility[dataGridKey] };
  } else {
    columnsVisibilyToUse = { ...intitialColumnsHidden };
  }
  //if no id is not specifically set to true, hide
  columnsVisibilyToUse = {
    ...columnsVisibilyToUse,
    id: columnVisibility[dataGridKey]?.id || false
  };

  const memorizedColumns = useMemo(
    () =>
      columns.map((col) => {
        if (col.type === 'number') {
          return {
            ...col,
            filterOperators: getGridNumericOperators().filter(
              (operator) => operator.value !== 'isAnyOf'
            ),
            valueFormatter: passThroughValueFormatter
          };
        }
        if (!col.type) {
          return {
            ...col,
            filterOperators: getGridStringOperators().filter(
              (operator) => operator.value !== 'isAnyOf'
            )
          };
        }
        return {
          ...col
        };
      }),
    [columns]
  );

  return (
    <StyledDataGridPro
      disableRowSelectionExcludeModel
      showToolbar
      getRowHeight={() => 'auto'}
      autoHeight={!height}
      style={{ height: height || 'initial' }}
      key={rerenderColumns}
      columns={memorizedColumns}
      density={density[dataGridKey] ?? 'standard'}
      minHeight={getGridDensityInPixels(density[dataGridKey])}
      apiRef={ref as any}
      initialState={{
        columns: {
          columnVisibilityModel: columnsVisibilyToUse,
          orderedFields: columnOrder[dataGridKey]
        },
        sorting: {
          sortModel: sorting[dataGridKey]
        },
        filter: {
          filterModel: getValidFilter(filtering[dataGridKey], columns)
        }
      }}
      onDensityChange={(newDensity) => {
        customDispatch({
          action: setDensity,
          actionParameters: [dataGridKey, newDensity],
          disableSuccessMessage: true
        });
      }}
      onSortModelChange={(model) => {
        customDispatch({
          action: setSorting,
          actionParameters: [dataGridKey, model],
          disableSuccessMessage: true
        });
      }}
      onFilterModelChange={(model) => {
        if (model.items.length === 0) {
          model.items = defaultFiltering[dataGridKey].items;
        }
        customDispatch({
          action: setFiltering,
          actionParameters: [dataGridKey, model],
          disableSuccessMessage: true
        });
      }}
      onColumnVisibilityModelChange={(model) => {
        customDispatch({
          action: setColumnVisibility,
          actionParameters: [dataGridKey, model],
          disableSuccessMessage: true
        });
      }}
      onColumnOrderChange={() => {
        const allColumns = ref.current?.getAllColumns() ?? [];
        const newColumnOrder = allColumns.map((column) => column.field);
        customDispatch({
          action: setColumnOrder,
          actionParameters: [dataGridKey, newColumnOrder],
          disableSuccessMessage: true
        });
      }}
      pinnedColumns={getPinnedFields(pinnedColumns[dataGridKey])}
      onPinnedColumnsChange={(pinnedColumns) => {
        customDispatch({
          action: setPinnedColumns,
          actionParameters: [dataGridKey, pinnedColumns],
          disableSuccessMessage: true
        });
      }}
      rowSelectionModel={normalizedRowSelectionModel}
      onRowSelectionModelChange={handleRowSelectionModelChange}
      disableRowSelectionOnClick={disableRowSelectionOnClick}
      {...restOther}
      slots={{
        toolbar: CustomGridToolbarSlot,
        pagination: CustomPagination,
        loadingOverlay: CustomLoadingOverlay
      }}
      slotProps={{
        toolbar: {
          dataGridKey,
          initialFilterModel,
          intitialColumnsHidden,
          setRerenderColumns
        }
      }}
    />
  );
}

export type CustomServerDataGridProps = {
  setUpdateDataGrid?: Dispatch<SetStateAction<number>>;
  dataGridKey: DataGridKey;
  intitialColumnsHidden?: GridColumnVisibilityModel;
  initialFilterModel?: GridFilterModel;
  height?: string;
  other?: any;
  modifySortModelOnChange?: (newModel: GridSortModel) => GridSortModel;
  modifyFilterModelOnChange?: (newModel: GridFilterModel) => GridFilterModel;
};

export function CustomServerDataGrid({
  setUpdateDataGrid,
  dataGridKey,
  columns,
  intitialColumnsHidden,
  initialFilterModel,
  height,
  modifySortModelOnChange = (sortModel) => sortModel,
  modifyFilterModelOnChange = (filterModel) => filterModel,
  ...other
}: CustomServerDataGridProps & DataGridProps) {
  const {
    selectionModel: legacySelectionModel,
    onSelectionModelChange: legacyOnSelectionModelChange,
    rowSelectionModel: controlledRowSelectionModel,
    onRowSelectionModelChange: controlledOnRowSelectionModelChange,
    disableRowSelectionOnClick,
    onPaginationModelChange,
    pageSizeOptions,
    ...restOther
  } = other as any;

  // The DataGrid print export temporarily sets pageSize to the number of currently loaded
  // rows. When there are fewer loaded rows than the current pageSize (e.g. 15 rows loaded
  // with pageSize=20), this new value won't match any pageSizeOption, triggering a server
  // refetch that sets loading=true and renders the loading overlay in the printed output, hiding the data.
  const handlePaginationModelChange = onPaginationModelChange
    ? (model: any) => {
        if (pageSizeOptions?.length && !pageSizeOptions.includes(model.pageSize)) {
          return;
        }
        onPaginationModelChange(model);
      }
    : undefined;
  const customDispatch = useCustomDispatchWithoutPromise();
  const ref = useRef<GridApiPro | null>(null);
  const { density, sorting, filtering, columnVisibility, pinnedColumns, columnOrder } = useSelector(
    (state: RootState) => state.datagrid
  );
  const normalizedRowSelectionModel = normalizeRowSelectionModel(
    controlledRowSelectionModel ?? legacySelectionModel
  );
  const handleRowSelectionModelChange =
    controlledOnRowSelectionModelChange || legacyOnSelectionModelChange
      ? (newModel: GridRowSelectionModel) => {
          controlledOnRowSelectionModelChange?.(newModel);
          if (legacyOnSelectionModelChange) {
            legacyOnSelectionModelChange(Array.from(newModel.ids ?? []));
          }
        }
      : undefined;
  //to ensure the density is only updated once, use local state for grid density, and update Redux state after
  const lastUsedDataGridKeyRef = useRef(dataGridKey);
  const [gridDensity, setGridDensity] = useState<GridDensity | undefined>(density[dataGridKey]);
  const [sortModel, setSortModel] = useState<GridSortModel | undefined>(sorting[dataGridKey]);
  const [filterModel, setFilterModel] = useState<GridFilterModel | undefined>(
    filtering[dataGridKey]
  );

  //if the datagridkey is changed, update density, filtering, and sorting
  useEffect(() => {
    if (lastUsedDataGridKeyRef.current === dataGridKey) return;
    setGridDensity(density[dataGridKey]);
    setSortModel(sorting[dataGridKey]);
    setFilterModel(filtering[dataGridKey]);
    lastUsedDataGridKeyRef.current = dataGridKey;
  }, [dataGridKey, density, filtering, sorting]);

  useEffect(() => {
    if (gridDensity !== density[dataGridKey]) {
      customDispatch({
        action: setDensity,
        actionParameters: [dataGridKey, gridDensity],
        disableSuccessMessage: true
      });
    }
  }, [gridDensity, density, dataGridKey, customDispatch]);

  useEffect(() => {
    if (JSON.stringify(sortModel) !== JSON.stringify(sorting[dataGridKey])) {
      if (lastUsedDataGridKeyRef.current !== dataGridKey) return;
      customDispatch({
        action: setSorting,
        actionParameters: [dataGridKey, sortModel],
        disableSuccessMessage: true
      });
      setUpdateDataGrid?.((count) => count + 1);
    }
  }, [sortModel, sorting, dataGridKey, setUpdateDataGrid, customDispatch]);

  useEffect(() => {
    if (JSON.stringify(filterModel) !== JSON.stringify(filtering[dataGridKey])) {
      if (lastUsedDataGridKeyRef.current !== dataGridKey) return;
      customDispatch({
        action: setFiltering,
        actionParameters: [dataGridKey, filterModel],
        disableSuccessMessage: true
      });
      setUpdateDataGrid?.((count) => count + 1);
    }
  }, [filterModel, filtering, dataGridKey, setUpdateDataGrid, customDispatch]);

  //create columnsVisibilityModel
  let columnsVisibilyToUse = {};
  //if no visibility is set, use initial visibility (if any)
  if (columnVisibility[dataGridKey]) {
    columnsVisibilyToUse = { ...columnVisibility[dataGridKey] };
  } else {
    columnsVisibilyToUse = { ...intitialColumnsHidden };
  }
  //if no id is not specifically set to true, hide
  columnsVisibilyToUse = {
    ...columnsVisibilyToUse,
    id: columnVisibility[dataGridKey]?.id || false
  };

  const [rerenderColumns, setRerenderColumns] = useState(0);

  //remove filteroperator isAnyOf
  const modifiedColumns = useMemo(
    () =>
      columns.map((col) => {
        if (col.filterOperators) {
          return {
            ...col
          };
        }
        if (col.type === 'singleSelect') {
          return {
            ...col,
            filterOperators: getGridSingleSelectOperators().filter(
              (operator) => operator.value === 'is'
            )
          };
        }
        if (col.type === 'number') {
          return {
            ...col,
            filterOperators: getGridNumericOperators().filter(
              (operator) => operator.value !== 'isAnyOf'
            ),
            valueFormatter: passThroughValueFormatter
          };
        }
        if (!col.type) {
          return {
            ...col,
            filterOperators: getGridStringOperators().filter(
              (operator) => operator.value !== 'isAnyOf'
            )
          };
        }
        if (col.type === 'boolean') {
          return {
            ...col,
            filterOperators: getGridBooleanOperators()
          };
        }

        return {
          ...col
        };
      }),
    [columns]
  );

  const previousFilterModelRef = useRef<GridFilterModel | undefined>(undefined);

  const handleFilterModelChange = (newModel: GridFilterModel) => {
    if (JSON.stringify(newModel) === JSON.stringify(filterModel)) return;
    if (lastUsedDataGridKeyRef.current !== dataGridKey) return;

    const prevModel = previousFilterModelRef.current;

    if (prevModel && prevModel.items.length === newModel.items.length) {
      newModel.items.forEach((item, index) => {
        const prevItem = prevModel.items[index];
        //if there was a prevous column selected, and the column was changed, reset value to avoid type conflicts
        if (prevItem && prevItem.field !== item.field && item.value !== undefined) {
          item.value = undefined;
        }
      });
    }

    previousFilterModelRef.current = newModel;
    setFilterModel(newModel);
  };

  const resolveColumnVisibilityModel = (model: GridColumnVisibilityModel) => {
    const currentVisibility = columnsVisibilyToUse as GridColumnVisibilityModel;
    const hideableColumns = columns.filter((column) => column.hideable !== false);
    const nonHideableColumns = new Set(
      columns.filter((column) => column.hideable === false).map((column) => column.field)
    );
    const columnFields = new Set(columns.map((column) => column.field));
    const modelKeys = Object.keys(model);
    const columnModelKeys = modelKeys.filter((field) => columnFields.has(field));
    const isShowAll = modelKeys.length === 0;
    const isPanelToggleAll = isPanelToggleAllModel(
      model,
      hideableColumns,
      nonHideableColumns,
      columnModelKeys
    );
    const hasVisibleHideable = hasVisibleHideableColumn(hideableColumns, currentVisibility);
    const shouldShowAll = isShowAll || (isPanelToggleAll && !hasVisibleHideable);
    const shouldHideAll = isPanelToggleAll && hasVisibleHideable;

    const nextModel: GridColumnVisibilityModel = isPanelToggleAll
      ? { ...currentVisibility }
      : { ...model };

    if (isPanelToggleAll) {
      modelKeys.forEach((field) => {
        if (!columnFields.has(field)) {
          nextModel[field] = model[field];
        }
      });
    }

    if (shouldShowAll && model[GRID_CHECKBOX_SELECTION_FIELD] === undefined) {
      nextModel[GRID_CHECKBOX_SELECTION_FIELD] = true;
    }
    if (shouldHideAll && model[GRID_CHECKBOX_SELECTION_FIELD] === undefined) {
      nextModel[GRID_CHECKBOX_SELECTION_FIELD] = false;
    }

    columns.forEach((column) => {
      const field = column.field;
      if (model[field] !== undefined) {
        nextModel[field] = model[field];
        return;
      }
      const columnIsHideable = column.hideable !== false;
      if (shouldShowAll && columnIsHideable) {
        nextModel[field] = true;
        return;
      }
      if (shouldHideAll && columnIsHideable) {
        nextModel[field] = false;
        return;
      }
      if (currentVisibility[field] !== undefined) {
        nextModel[field] = currentVisibility[field];
        return;
      }
      nextModel[field] = true;
    });

    const hasStoredVisibility = columnVisibility[dataGridKey] !== undefined;
    const shouldDefaultHideId =
      !hasStoredVisibility && model.id === undefined && intitialColumnsHidden?.id === false;
    if (shouldDefaultHideId) {
      nextModel.id = false;
    }

    return nextModel;
  };

  return (
    <StyledDataGridPro
      disableRowSelectionExcludeModel
      showToolbar
      getRowHeight={() => 'auto'}
      autoHeight={!height}
      style={{ height: height || 'initial' }}
      key={rerenderColumns}
      columns={modifiedColumns}
      density={density[dataGridKey] ?? 'standard'}
      minHeight={getGridDensityInPixels(gridDensity)}
      apiRef={ref as any}
      initialState={{
        columns: {
          orderedFields: columnOrder[dataGridKey]
        }
      }}
      onDensityChange={(newDensity) => {
        if (lastUsedDataGridKeyRef.current !== dataGridKey) return;
        setGridDensity(newDensity);
      }}
      sortingMode="server"
      sortModel={sorting[dataGridKey]}
      onSortModelChange={async (model) => {
        if (lastUsedDataGridKeyRef.current !== dataGridKey) return;
        setSortModel(modifySortModelOnChange(model));
      }}
      filterMode="server"
      filterModel={getValidFilter(filterModel ?? filtering[dataGridKey], columns)}
      onFilterModelChange={async (model) => {
        if (model.items.length === 0) {
          model.items = defaultFiltering[dataGridKey].items;
        }
        handleFilterModelChange(modifyFilterModelOnChange(model));
      }}
      columnVisibilityModel={columnsVisibilyToUse}
      onColumnVisibilityModelChange={(model) => {
        const resolvedModel = resolveColumnVisibilityModel(model);
        customDispatch({
          action: setColumnVisibility,
          actionParameters: [dataGridKey, resolvedModel],
          disableSuccessMessage: true
        });
      }}
      onColumnOrderChange={() => {
        const allColumns = ref.current?.getAllColumns() ?? [];
        const newColumnOrder = allColumns.map((column) => column.field);
        customDispatch({
          action: setColumnOrder,
          actionParameters: [dataGridKey, newColumnOrder],
          disableSuccessMessage: true
        });
      }}
      onPinnedColumnsChange={(pinnedColumns) => {
        customDispatch({
          action: setPinnedColumns,
          actionParameters: [dataGridKey, pinnedColumns],
          disableSuccessMessage: true
        });
      }}
      pinnedColumns={getPinnedFields(pinnedColumns[dataGridKey])}
      rowSelectionModel={normalizedRowSelectionModel}
      onRowSelectionModelChange={handleRowSelectionModelChange}
      disableRowSelectionOnClick={disableRowSelectionOnClick}
      {...restOther}
      {...(pageSizeOptions !== undefined ? { pageSizeOptions } : {})}
      onPaginationModelChange={handlePaginationModelChange}
      slots={{
        toolbar: CustomGridToolbarSlot,
        loadingOverlay: CustomLoadingOverlay
      }}
      slotProps={{
        toolbar: {
          dataGridKey,
          setGridDensity,
          initialFilterModel,
          setFilterModel,
          setSortModel,
          intitialColumnsHidden,
          setRerenderColumns,
          setUpdateDataGrid
        }
      }}
    />
  );
}

const StyledDataGridPro = styled(DataGridPro)(({ minHeight }: { minHeight: number }) => ({
  width: '100%',
  '.MuiDataGrid-row': {
    minHeight: `${minHeight}px !important`
  },
  '& .MuiDataGrid-cell': {
    display: 'flex',
    alignItems: 'center'
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    whiteSpace: 'normal',
    lineHeight: 'normal'
  },
  '& .MuiDataGrid-columnHeader': {
    // Forced to use important since overriding inline styles
    height: 'unset !important'
  },
  '& .MuiDataGrid-columnHeaders': {
    // Forced to use important since overriding inline styles
    maxHeight: '168px !important'
  }
}));
